import { Children, ReactNode, forwardRef } from "react";
import {
  Animated,
  View as RNView,
  ViewProps as RNViewProps,
  StyleSheet,
  ViewStyle,
} from "react-native";

import {
  BMComponentsType,
  BMPrimitives,
  useBMTheme,
} from "@traveloka/district-bloom";

const { BMSize } = BMPrimitives;
type Spacing = keyof typeof BMSize;

export type ViewRef = RNView;

export type BMViewProps = {
  children?: ReactNode;
  /**
   * Defines `align-items` style property.
   * @default 'stretch'
   */
  align?: "start" | "end" | "center" | "stretch" | "baseline";
  /**
   * Defines `justify-content` style property.
   * @default 'start'
   */
  justify?: "start" | "end" | "center" | "around" | "between" | "evenly";
  /**
   * Set`flex-direction` as `row-reverse` or `column-reverse`
   */
  reverse?: boolean;
  /**
   * Set `flex-direction` as `row` or `column`
   */
  row?: boolean;
  /**
   * Defines the space between the children.
   * ```
   * xs : 4
   * sm : 8
   * md : 12
   * lg : 16
   * xl : 24
   * xl2: 32
   * xl3: 40
   * xl4: 48
   * xl5: 64
   * xl6: 72
   * ```
   */
  spacing?: Spacing;
  /**
   * Set `flex: 1` to fill the remaining space.
   */
  flex?: number | boolean;
  /**
   * Force testID without prefix
   */
  forcedTestID?: string;
  animated?: true;
  variant?: "VIEW" | "CARD";
} & RNViewProps;

export default forwardRef<HTMLElement | RNView, BMViewProps>(function BMView(
  props,
  ref
) {
  const {
    align,
    animated,
    flex,
    forcedTestID,
    justify,
    reverse,
    row,
    spacing,
    style,
    testID,
    variant = "VIEW",
    ...rest
  } = props;

  const { BMComponents } = useBMTheme();

  let children = props.children;
  if (spacing !== undefined) {
    const property = row ? "width" : "height";
    children = intersperse(Children.toArray(props.children), (index) => (
      <RNView
        aria-hidden
        key={"s." + index}
        style={{ flexShrink: 0, [property]: BMSize[spacing] }}
      />
    ));
  }

  let directionStyle: ViewStyle = {};
  if (row && reverse) {
    directionStyle = directionStyles["row-reverse"];
  } else if (row) {
    directionStyle = directionStyles["row"];
  } else if (reverse) {
    directionStyle = directionStyles["column-reverse"];
  }

  const variantStyles = createVariantStyles(BMComponents);

  const rootStyles = [
    directionStyle,
    typeof align === "string" && alignmentStyles[align],
    typeof justify === "string" && justifyStyles[justify],
    typeof flex === "boolean" && flex
      ? { flex: 1 }
      : typeof flex === "number" && { flex },
    variantStyles[variant],
    style,
  ];

  let testIDValue = undefined;
  if (testID) {
    testIDValue = ["view", testID].join("-");
  } else if (forcedTestID) {
    testIDValue = forcedTestID;
  }

  if (animated) {
    return (
      <Animated.View
        {...rest}
        // @ts-expect-error -- ref type for HTMLElement is correct because we are using react-native-web
        ref={ref}
        style={rootStyles}
        testID={testIDValue}
      >
        {children}
      </Animated.View>
    );
  }

  return (
    <RNView
      {...rest}
      // @ts-expect-error -- ref type for HTMLElement is correct because we are using react-native-web
      ref={ref}
      style={rootStyles}
      testID={testIDValue}
    >
      {children}
    </RNView>
  );
});

const directionStyles = StyleSheet.create({
  row: { flexDirection: "row" },
  "row-reverse": { flexDirection: "row-reverse" },
  "column-reverse": { flexDirection: "column-reverse" },
});

const alignmentStyles = StyleSheet.create({
  start: { alignItems: "flex-start" },
  end: { alignItems: "flex-end" },
  center: { alignItems: "center" },
  baseline: { alignItems: "baseline" },
  stretch: { alignItems: "stretch" },
});

const justifyStyles = StyleSheet.create({
  start: { justifyContent: "flex-start" },
  end: { justifyContent: "flex-end" },
  center: { justifyContent: "center" },
  around: { justifyContent: "space-around" },
  between: { justifyContent: "space-between" },
  evenly: { justifyContent: "space-evenly" },
});

function createVariantStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    VIEW: {},
    CARD: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderRadius: BMPrimitives.BMRadius.md,
    },
  });
}

// Functions
function intersperse<T>(array: T[], callback: (index: number) => T): T[] {
  const lastIndex = array.length - 1;

  return array.reduce((arr, value, index) => {
    arr.push(value);

    if (index !== lastIndex) {
      arr.push(callback(index));
    }

    return arr;
  }, [] as T[]);
}
